import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import useUnitProvider from '/@/core/context/unit';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { parseUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getUser } from '/@/store/session/selectors';
import type { Grower } from '/@/types/growers';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import { reverse } from '/@/utils/router';
import GrowerNameCell from '/@/views/dashboard/growers/components/growers-list/components/grower-name-cell';

const RecommendationsRvRow = ({ rv, cropFilter, year }: { rv: Grower; cropFilter: string; year?: number }) => {
    const navigate = useNavigate();
    const units = useUnitProvider();
    const user = useSelector(getUser);

    const growersDashboardQuery = useGrowersDashboard({
        node: rv.id,
        ...getNodeScopeFilters('rvs'),
    });

    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();

    // Transform API data to component format
    const transformGrowerData = useCallback(
        (grower: Grower) => {
            const parsedUserNode = parseUserNode(grower, user);
            const fields = grower.fields || {};
            const totalFields = fields.count || 0;

            // Generate suggestions
            const fieldviewConnected = grower.user?.authProviders?.includes('fieldview');

            return {
                id: grower.id,
                name: parsedUserNode.isSelf ? _('Mi cuenta') : parsedUserNode.name,
                location: parsedUserNode.location,
                totalFields,
                totalArea: convertAreaFromM2(grower?.fields.totalArea || 0, units).toString(),
                fieldviewConnected: fieldviewConnected,
                hasAssignedUser: grower.user !== null,
                totalHybridRecommendations:
                    grower.recommendationStatistics?.countByType?.corn ||
                    (grower.recommendationStatistics?.countByType?.soy || 0) +
                        (grower.recommendationStatistics?.countByType?.density || 0),
                totalCropProtectionRecommendations:
                    grower.recommendationStatistics?.countByType?.funguicide ||
                    (grower.recommendationStatistics?.countByType?.insecticide || 0) +
                        (grower.recommendationStatistics?.countByType?.herbicide || 0),
                totalFieldsWithRecommendations: grower.recommendationStatistics.fieldsCount,
                phone: parsedUserNode.phone,
                email: parsedUserNode.email,
                isSelf: parsedUserNode.isSelf,
                totalGrowersCount: growersDashboardQuery.data?.count || 0,
                totalGrowersCountWithFieldview: growersDashboardQuery.data?.withFieldview || 0,
            };
        },
        [units, user, growersDashboardQuery.data?.withFieldview, growersDashboardQuery.data?.count]
    );

    const transformedGrower = useMemo(() => transformGrowerData(rv), [rv, transformGrowerData]);

    return (
        <TableRow
            key={transformedGrower.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
                navigate(
                    reverse('RVs.detail', {
                        params: {
                            rvId: transformedGrower.id,
                        },
                        qs: {
                            tab: 'current-campaign',
                        },
                    })
                );
            }}
        >
            <TableCell width='20%'>
                <GrowerNameCell
                    name={transformedGrower.name}
                    location={transformedGrower.location}
                    hasAssignedUser={transformedGrower.hasAssignedUser}
                />
            </TableCell>
            <TableCell width='20%'>
                <TableTemplateCell
                    title={_('{{count}} Productores', { count: transformedGrower.totalGrowersCount })}
                    subtitle={_('{{value}} con Fieldview', { value: transformedGrower.totalGrowersCountWithFieldview })}
                />
            </TableCell>
            <TableCell width='20%'>
                <TableTemplateCell
                    title={_('{{count}} Lote', { count: transformedGrower.totalFields })}
                    subtitle={_('Superficie total: {{value}}', { value: transformedGrower.totalArea })}
                />
            </TableCell>
            <TableCell width='30%'>
                <TableTemplateCell
                    title={
                        !transformedGrower.totalHybridRecommendations
                            ? _('Sin recomendaciones generadas')
                            : _('{{count}} Recomendaciones', { count: transformedGrower.totalHybridRecommendations })
                    }
                />
            </TableCell>
            <TableCell width='30%'>
                <TableTemplateCell
                    title={
                        !transformedGrower.totalCropProtectionRecommendations
                            ? _('Sin recomendaciones de protección de cultivos generadas')
                            : _('{{count}} Recomendaciones', {
                                  count: transformedGrower.totalCropProtectionRecommendations,
                              })
                    }
                />
            </TableCell>
            <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                <IconButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleClick(e);
                    }}
                >
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    open={!!anchorEl}
                    onClose={(e) => {
                        e.stopPropagation();
                        handleClose();
                    }}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {[
                        {
                            label: _('Ir al detalle del RV'),
                            onClick: () => {
                                navigate(
                                    reverse('RVs.detail', {
                                        params: {
                                            rvId: transformedGrower.id,
                                        },
                                    })
                                );
                            },
                            disabled: false,
                        },
                    ].map((item, index) => (
                        <MenuItem
                            key={index}
                            onClick={(e) => {
                                e.stopPropagation();
                                item.onClick(e);
                                handleClose();
                            }}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Menu>
            </TableCell>
        </TableRow>
    );
};

export default RecommendationsRvRow;
